/**
 * Comment renderer. Runs on the real page: every front-page story gets
 * its canned thread under the teaser. Nothing here knows the paywall
 * exists, which is deliberate — the comment desk and the business desk
 * do not speak, and the front page is better for it.
 */

import { postLocal, threadFor, type Comment } from "./store";
import { t } from "./strings";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function when(ts: number): string {
  const mins = Math.max(1, Math.round((Date.now() - ts) / 60_000));
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} d ago`;
}

function renderOne(c: Comment, universe: string, depth: number): string {
  const replies = (c.replies ?? []).map((r) => renderOne(r, universe, depth + 1)).join("");
  return `
  <div class="wire-comment wire-comment--d${depth}">
    <div class="wire-comment__meta">
      <span class="wire-comment__author">${esc(c.author)}</span>
      <span class="wire-comment__uni">u/${esc(c.universe)}</span>
      <span class="wire-comment__when">${when(c.ts)}</span>
    </div>
    <div class="wire-comment__body">${esc(c.body)}</div>
    ${replies}
  </div>`;
}

export function renderThread(mount: HTMLElement, storyId: string): void {
  const universe = document.documentElement.dataset.universe ?? "A-1";
  const thread = threadFor(storyId);
  const html = `
    <h3 class="wire-comments__title">${esc(t(universe, "comments.title"))}</h3>
    ${
      thread.length === 0
        ? `<p class="wire-comments__empty">${esc(t(universe, "comments.empty"))}</p>`
        : thread.map((c) => renderOne(c, universe, 0)).join("")
    }`;
  mount.innerHTML = html;
}

export function renderPostBox(mount: HTMLElement, storyId: string): void {
  const universe = document.documentElement.dataset.universe ?? "A-1";
  mount.innerHTML = `
    <form class="wire-comment-form" data-story="${esc(storyId)}">
      <input class="wire-comment-form__author" type="text" placeholder="handle" maxlength="32" />
      <textarea class="wire-comment-form__body" rows="2" placeholder="${esc(t(universe, "comments.placeholder"))}"></textarea>
      <button class="wire-comment-form__submit" type="submit">${esc(t(universe, "comments.post"))}</button>
      <span class="wire-comment-form__note">${esc(t(universe, "comments.pending"))}</span>
    </form>`;
  const form = mount.querySelector("form");
  form?.addEventListener("submit", (e) => {
    e.preventDefault();
    const authorEl = mount.querySelector<HTMLInputElement>(".wire-comment-form__author");
    const bodyEl = mount.querySelector<HTMLTextAreaElement>(".wire-comment-form__body");
    if (!authorEl || !bodyEl || !bodyEl.value.trim()) return;
    const c = postLocal(storyId, authorEl.value.trim() || "reader", bodyEl.value.trim());
    (c as Comment & { story: string }).story = storyId;
    const note = mount.querySelector(".wire-comment-form__note");
    if (note) note.textContent = t(universe, "comments.moderated");
    bodyEl.value = "";
  });
}
